import {
    EmptyFolderDocument,
    EmptyFolderMutation,
    EmptyFolderMutationVariables,
} from '../graphql/__generated__/EmptyFolderMutation.interface';
import { getApolloClient } from 'owa-apollo';
import { BulkActionCustomDataBase, lazyfindBulkActionItemAction } from 'owa-bulk-action-store';
import { getISOString, OwaDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isFolderOfTreeType } from 'owa-folders';
import { SHARED_FOLDERS_TREE_TYPE } from 'owa-folders-constants';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getMailboxInfoForFolderId } from '../util/getMailboxInfoForFolderId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';

type EmptyFolderResult = EmptyFolderMutation['emptyFolder'];

/**
 * Create and submit the empty folder action for the specified folder
 * @param folderId the folderId of the folder to be emptied
 * @param disposalType the disposal type
 * @param deleteSubFolder whether to delete subfolder
 * @param clientLastSyncTime the client last sync time watermark
 * @param itemIdsToExclude item ids to exclude from empty folder operation
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is supported for this empty folder request
 */
export async function emptyFolderService(
    folderId: string,
    disposalType: DisposalType,
    deleteSubFolder: boolean,
    clientLastSyncTime: OwaDate | null | undefined,
    itemIdsToExclude: string[],
    shouldSupportBulkActions: boolean,
    bulkActionCustomData?: BulkActionCustomDataBase
): Promise<void> {
    let emptyFolderPromise = null;

    const suppressReadReceipt =
        getUserConfiguration().UserOptions.ReadReceipt != 'AlwaysSend' ||
        isFolderOfTreeType(folderId, SHARED_FOLDERS_TREE_TYPE);

    const distinguishedFolderIdName = folderIdToName(folderId);
    const folderIdToEmpty =
        distinguishedFolderIdName == 'none' ? folderId : distinguishedFolderIdName;

    const mailboxInfo = getMailboxInfoForFolderId(
        folderIdToEmpty,
        false /*routeToAuxIfAuxArchive*/
    );

    let input: EmptyFolderMutationVariables = {
        id: folderId,
        disposalType: disposalType,
        deleteSubFolder: deleteSubFolder,
        clientLastSyncTime: clientLastSyncTime && getISOString(clientLastSyncTime),
        itemIdsToExclude: itemIdsToExclude,
        suppressReadReceipt: suppressReadReceipt,
        bulkActionInput: {
            supportBulkActions: shouldSupportBulkActions,
            bulkActionBatchSize: isFeatureEnabled('tri-emptyFolderBulkActionBatchSize') ? 200 : 10,
            bulkActionCustomData: bulkActionCustomData && JSON.stringify(bulkActionCustomData),
        },
        mailboxInfo: mailboxInfo,
    };

    emptyFolderPromise = exportedHelperFunctions.invokeEmptyFolderMutation(input);

    return emptyFolderPromise.then(() => {
        lazyfindBulkActionItemAction.importAndExecute(); // Need this to update Bulk Action Store when Notification Channel is not set up.
    });
}

/**
 * Invoke the empty folder mutation
 * @param input input parameters for the mutation
 */
const invokeEmptyFolderMutationFn = async function invokeEmptyFolderMutation(
    input: EmptyFolderMutationVariables
): Promise<EmptyFolderResult> {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: input,
        mutation: EmptyFolderDocument,
    });

    return result?.data?.emptyFolder;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeEmptyFolderMutation: invokeEmptyFolderMutationFn,
};
